import { useState, useEffect } from 'react';
import Modal from '../shared/Modal';
import { sendAssignmentEmail, upsertAssignment } from '../../utils/api';

export default function SendRequestsModal({ open, onClose, items, teamName, templateId, senderEmail, canSend, onSent, showToast }) {
  const [sending, setSending] = useState(false);
  // Per-person selection — leads choose who gets a request (default: everyone).
  const [checked, setChecked] = useState(() => new Set());

  // Group by person — one email per SL, not one per slot. A person filling
  // multiple positions gets a single email linking to their page where they
  // review ALL their requests.
  const byPerson = {};
  for (const item of items) {
    if (!byPerson[item.personId]) {
      byPerson[item.personId] = { personId: item.personId, name: item.personName, email: item.personEmail, positions: [], items: [], teams: new Set() };
    }
    byPerson[item.personId].positions.push(`${item.blockTitle}: ${item.positionLabel}`);
    byPerson[item.personId].items.push(item);
    if (item.teamName) byPerson[item.personId].teams.add(item.teamName);
  }
  const people = Object.values(byPerson);

  // Everyone starts selected whenever the modal opens with a fresh list.
  useEffect(() => {
    if (open) setChecked(new Set(items.map(i => i.personId)));
  }, [open, items]);

  const toggleChecked = (personId) => setChecked(prev => {
    const next = new Set(prev);
    if (next.has(personId)) next.delete(personId); else next.add(personId);
    return next;
  });
  const selectedPeople = people.filter(p => checked.has(p.personId));
  const allChecked = people.length > 0 && selectedPeople.length === people.length;

  async function handleSend() {
    if (!canSend) { showToast('Only @thebelonging.co staff can send emails.'); return; }
    if (selectedPeople.length === 0) { showToast('Select at least one person to send to.'); return; }
    setSending(true);
    const now = new Date().toISOString();
    const sentIds = [];
    let emailed = 0;

    // One email per SELECTED person, then mark their assignments pending.
    await Promise.allSettled(
      selectedPeople
        .filter(p => p.email)
        .map(async (p) => {
          try {
            const blockName = p.items.length === 1
              ? p.items[0].blockTitle
              : `${p.items.length} serving positions`;
            // Prefer the person's actual team(s) (admin-wide sends span teams).
            const personTeam = p.teams?.size ? Array.from(p.teams).join(' & ') : teamName;
            await sendAssignmentEmail(p.email, p.name, p.personId, blockName, personTeam, senderEmail, templateId);
            emailed++;
            for (const item of p.items) {
              if (item.fullAssignment) {
                await upsertAssignment({ ...item.fullAssignment, status: 'pending', sentAt: now });
              }
              sentIds.push(item.assignmentId);
            }
          } catch (err) {
            console.error('Send failed for', p.name, err);
          }
        })
    );

    showToast(`${emailed} email${emailed !== 1 ? 's' : ''} sent.`);
    setSending(false);
    // Pass sent IDs + timestamp so parent can update state without a reload
    if (onSent) onSent(sentIds, now);
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Send Requests"
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={sending}>Cancel</button>
          <button className="btn btn-dark" onClick={handleSend} disabled={sending || selectedPeople.length === 0}>
            {sending ? 'Sending…' : `Send ${selectedPeople.length} Email${selectedPeople.length !== 1 ? 's' : ''}`}
          </button>
        </>
      }
    >
      <div className="notice-bar" style={{ marginBottom: 20 }}>
        Sending requests for <strong>{teamName}</strong> · emails sent from <strong>your account</strong> ({senderEmail || 'your email'}).
        Each person receives a personalized assignment confirmation email.
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--muted)' }}>
          Unsent — {selectedPeople.length} of {people.length} selected
        </span>
        {people.length > 0 && (
          <button
            className="btn btn-xs"
            onClick={() => setChecked(allChecked ? new Set() : new Set(items.map(i => i.personId)))}
          >
            {allChecked ? 'Deselect all' : 'Select all'}
          </button>
        )}
      </div>
      <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginBottom: 12 }}>
        These people are assigned to positions but haven't been notified yet. Uncheck anyone you don't want to email right now.
      </div>

      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', maxHeight: 280, overflowY: 'auto' }}>
        {people.map((p, i) => (
          <label
            key={p.email + i}
            style={{ padding: '10px 16px', borderBottom: i < people.length - 1 ? '1px solid var(--border)' : 'none', display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', background: checked.has(p.personId) ? 'var(--white)' : 'var(--off)' }}
          >
            <input
              type="checkbox"
              checked={checked.has(p.personId)}
              onChange={() => toggleChecked(p.personId)}
              style={{ accentColor: 'var(--black)', marginTop: 2, flexShrink: 0 }}
            />
            <div style={{ flex: 1, opacity: checked.has(p.personId) ? 1 : 0.55 }}>
              <div style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>{p.name}</div>
              <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginTop: 2 }}>
                {p.positions.join(' · ')}
              </div>
            </div>
            <span style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)', flexShrink: 0 }}>{p.email}</span>
          </label>
        ))}
        {items.length === 0 && (
          <div style={{ padding: '16px', textAlign: 'center', fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)' }}>
            No unsent positions.
          </div>
        )}
      </div>

      {!canSend && (
        <div style={{ marginTop: 12, background: '#fffbf0', border: '1px solid #d4920a', borderRadius: 'var(--radius)', padding: '11px 14px', fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 300, color: '#7a4f0a' }}>
          Only <strong>@thebelonging.co</strong> accounts can send emails.
        </div>
      )}
    </Modal>
  );
}
